/*
 * Settings, Automatic messages: one readable row per automation. Each opens a page with the messages it sends, written out
 * with a real client and job, then Edit. Every one is off until the owner turns it on, and turning one on says to turn off
 * the same one in Jobber.
 *
 * The reminders also live in their own section's settings (owner, 2026-10-30): quote follow-ups in Quote settings, invoice
 * and debt reminders and receipts in Invoice settings, visit reminders in Schedule settings, the request reply in Request
 * settings. The page shows each message the way the client gets it, a text on a phone or an email, and fills in while the
 * owner types.
 */
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import {
  AUTOMATIONS,
  fillAutoWords,
  stepEmail,
  stepText,
  stepWhen,
  textParts,
  type AutomationDetail,
  type AutomationKind,
  type AutomationRow,
  type AutomationStep,
} from "@steward/shared";
import { useAutomation, useAutomations, useSaveAutomation } from "../../api/hooks";
import { useSession } from "../../app/session";
import { InputText } from "../../components/fields";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/overlays";
import { Banner, Button, Card, Details, Skeleton, StatusLabel, Switch } from "../../components/parts";
import { errText } from "../../components/work";

const CHANNEL = { sms: "Text", email: "Email" } as const;

/** Which messages show in each section's settings, and where that section's settings are. */
export const MESSAGE_SECTIONS = {
  quotes: { label: "Quote settings", to: "/quotes/settings", kinds: ["quote_followup"] },
  invoices: {
    label: "Invoice and payment settings",
    to: "/invoices/settings",
    kinds: ["invoice_reminder", "debt_reminder", "receipt"],
  },
  schedule: { label: "Schedule settings", to: "/schedule/settings", kinds: ["job_booked", "visit_reminder", "visit_report"] },
  requests: { label: "Request settings", to: "/requests/settings", kinds: ["booking_confirm"] },
} as const satisfies Record<string, { label: string; to: string; kinds: readonly AutomationKind[] }>;
export type MessageSection = keyof typeof MESSAGE_SECTIONS;

/** The fill-in words in plain names, for the buttons that put them in. */
const FILL_NAMES: Record<string, string> = {
  "{first}": "First name",
  "{name}": "Full name",
  "{job}": "Job name",
  "{service}": "Service",
  "{soon}": "When (tomorrow, Monday)",
  "{details}": "Visit details",
  "{business}": "Business name",
  "{phone}": "Business phone",
  "{number}": "Number",
  "{total}": "Total",
  "{overdue}": "Past due words",
  "{date}": "Invoice date",
  "{price}": "Price",
  "{crew}": "Crew name",
};

const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** One short line of when each message goes: "Text 1 day before at 10:00 AM, email 7 days before at 10:00 AM". */
function whenLine(a: AutomationRow): string {
  if (a.steps.length > 2) return `${String(a.steps.length)} messages, the first ${lowerFirst(stepWhen(a.kind, a.steps[0]!))}`;
  return a.steps
    .map((s, i) => `${i ? CHANNEL[s.channel].toLowerCase() : CHANNEL[s.channel]} ${lowerFirst(stepWhen(a.kind, s))}`)
    .join(", ");
}

function AutoStatus({ a }: { a: AutomationRow }) {
  if (a.kind === "past_clients" || a.kind === "plan_care")
    return <StatusLabel status="informative">In Marketing, Campaigns</StatusLabel>;
  if (!a.ready) return <StatusLabel status="inactive">Coming soon</StatusLabel>;
  return a.on ? <StatusLabel status="success">On</StatusLabel> : <StatusLabel status="inactive">Off</StatusLabel>;
}

export function AutomationsSettings() {
  const list = useAutomations();
  if (!list.data) return <Skeleton rows={8} />;
  return (
    <Card title="Automatic messages" flush>
      <p className="muted auto-intro">
        Each one is off until you turn it on. Texts wait for 8 AM to 8 PM, and marketing texts for 9 AM to 7 PM.
      </p>
      <ul className="auto-list">
        {list.data.automations.map((a) => (
          <li key={a.kind}>
            <Link to="/settings/automations/$kind" params={{ kind: a.kind }} className="setting-row">
              <span className="what">
                <b>{a.title}</b>
                <span>{a.what}</span>
              </span>
              <AutoStatus a={a} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** The reminders a section sends, at the top of its settings. Each row opens the message page inside the section. */
export function SectionMessages({ section }: { section: MessageSection }) {
  const list = useAutomations();
  const kinds: readonly AutomationKind[] = MESSAGE_SECTIONS[section].kinds;
  if (!list.data) return <Skeleton rows={2} />;
  const rows = list.data.automations.filter((a) => kinds.includes(a.kind));
  return (
    <Card id="messages" title="Reminders and messages" flush>
      <p className="muted auto-intro">What clients get by text and email. Open one to change the words and see how it looks.</p>
      <ul className="auto-list">
        {rows.map((a) => (
          <li key={a.kind}>
            <Link
              to={`${MESSAGE_SECTIONS[section].to}/messages/$kind` as const}
              params={{ kind: a.kind }}
              className="setting-row"
            >
              <span className="auto-row-icon" aria-hidden>
                <Icon name={a.steps.every((s) => s.channel === "email") ? "mail" : "message"} size={18} />
              </span>
              <span className="what">
                <b>{a.title}</b>
                <span>{whenLine(a)}</span>
              </span>
              <AutoStatus a={a} />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/** Messages the server starts when something happens (a request, a booking, a payment), not on a day count. */
function happensOnEvent(kind: AutomationKind): boolean {
  const info = AUTOMATIONS[kind];
  return info.repeatDays === null && info.steps.every((s) => s.day === 0 && s.time === null);
}

export function AutomationPage({
  kind,
  back = { to: "/settings/automations", label: "Automatic messages" },
}: {
  kind: AutomationKind;
  /** The link back, or null inside a section page, which has its own. */
  back?: { to: string; label: string } | null;
}) {
  const a = useAutomation(kind);
  const save = useSaveAutomation(kind);
  const toast = useToast();
  const { can } = useSession();
  const [editing, setEditing] = useState<AutomationStep[] | null>(null);
  const [picked, setPicked] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  if (!a.data) return <Skeleton rows={6} />;
  const d = a.data;
  const owner = can("settings.update");
  const steps = editing ?? d.steps;
  const at = Math.min(picked, steps.length - 1);
  const empty = steps.some((s) => !(s.channel === "email" ? s.email || s.text : s.text).trim());
  const changed = editing !== null && JSON.stringify(editing) !== JSON.stringify(d.steps);

  const turn = (on: boolean) =>
    save.mutate(
      { on },
      {
        onSuccess: (r) => {
          setNotice(r.notice);
          toast(on ? `${d.title} is on` : `${d.title} is off`);
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );

  const saveSteps = () => {
    if (!editing) return;
    save.mutate(
      { steps: editing },
      {
        onSuccess: () => {
          setEditing(null);
          toast("Saved");
        },
        onError: (e) => toast(errText(e), { kind: "error" }),
      },
    );
  };

  return (
    <>
      {back ? (
        <Link to={back.to} className="auto-back">
          {back.label}
        </Link>
      ) : null}
      {notice ? <Banner type="warning">{notice}</Banner> : null}
      <Card
        title={d.title}
        action={
          owner && d.ready ? (
            <Switch checked={d.on} label={d.on ? "On" : "Off"} disabled={save.isPending} onChange={turn} />
          ) : !d.ready ? (
            <StatusLabel status="inactive">Coming soon</StatusLabel>
          ) : (
            <StatusLabel status={d.on ? "success" : "inactive"}>{d.on ? "On" : "Off"}</StatusLabel>
          )
        }
      >
        <Details
          rows={[
            ["What it does", d.what],
            ["Counts days from", d.anchor],
            ["Kind", d.purpose === "marketing" ? "Marketing (9 AM to 7 PM, never to people who said no)" : "Service message"],
            ["Sent so far", String(d.sent)],
          ]}
        />
      </Card>

      <Card
        title={editing ? "Edit the messages" : "What it sends"}
        action={
          editing ? (
            <div className="row">
              <Button type="tertiary" size="small" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button size="small" loading={save.isPending} disabled={!changed || empty} onClick={saveSteps}>
                Save
              </Button>
            </div>
          ) : owner ? (
            <Button type="secondary" size="small" icon="edit" onClick={() => setEditing(d.steps.map((s) => ({ ...s })))}>
              Edit
            </Button>
          ) : null
        }
      >
        <div className="msg-studio">
          <div className="msg-left">
            {steps.length > 1 ? (
              <ol className="msg-steps">
                {steps.map((s, i) => (
                  <li key={i}>
                    <button type="button" aria-current={i === at} onClick={() => setPicked(i)}>
                      <span className={`msg-dot ${s.channel}`} aria-hidden>
                        <Icon name={s.channel === "email" ? "mail" : "message"} size={14} />
                      </span>
                      <span className="what">
                        <b>{whenOf(kind, s, i)}</b>
                        <span className="muted">
                          {CHANNEL[s.channel]}
                          {s.channel === "email" && s.subject ? `, ${fillAutoWords(s.subject, wordsFor(kind, s, d.words))}` : ""}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ol>
            ) : null}
            {editing ? (
              <StepFields
                key={at}
                kind={kind}
                d={d}
                step={editing[at]!}
                index={at}
                onChange={(patch) => setEditing(editing.map((s, j) => (j === at ? { ...s, ...patch } : s)))}
                onReset={() => {
                  const first = AUTOMATIONS[kind].steps[at];
                  if (first) setEditing(editing.map((s, j) => (j === at ? { ...first } : s)));
                }}
              />
            ) : (
              <p className="muted msg-hint">
                {steps.length > 1 ? "Pick a message to see it. " : ""}
                {owner ? "Edit changes the words and the day it goes." : "Only the owner can change these."}
              </p>
            )}
          </div>
          <Preview kind={kind} d={d} step={steps[at]!} index={at} />
        </div>
      </Card>
    </>
  );
}

/**
 * The sample words for one message. A visit reminder says when the visit is from the day it goes (the server's soonWords):
 * the sample visit is Tuesday, Nov 3.
 */
function wordsFor(kind: AutomationKind, s: AutomationStep, words: Record<string, string>): Record<string, string> {
  if (kind !== "visit_reminder") return words;
  const ahead = -s.day;
  const soon =
    ahead <= 0 ? "today" : ahead === 1 ? "tomorrow" : ahead < 7 ? "on Tuesday" : ahead === 7 ? "next week" : "on Tuesday, Nov 3";
  return { ...words, soon };
}

function whenOf(kind: AutomationKind, s: AutomationStep, i: number): string {
  if (kind === "booking_confirm" && i === 1) return "When the website gave a price";
  return stepWhen(kind, s);
}

function StepFields({
  kind,
  d,
  step: s,
  index,
  onChange,
  onReset,
}: {
  kind: AutomationKind;
  d: AutomationDetail;
  step: AutomationStep;
  index: number;
  onChange: (patch: Partial<AutomationStep>) => void;
  onReset: () => void;
}) {
  const info = AUTOMATIONS[kind];
  const box = useRef<HTMLTextAreaElement>(null);
  const subjectBox = useRef<HTMLInputElement>(null);
  const [lastBox, setLastBox] = useState<"none" | "body" | "subject">("none");
  const email = s.channel === "email";
  const body = email ? s.email || s.text : s.text;
  const setBody = (v: string) => onChange(email ? { email: v } : { text: v });
  const before = info.anchor === "the visit day";
  const full = email ? "" : stepText(kind, s, wordsFor(kind, s, d.words));
  const parts = textParts(full);

  /** Puts a fill-in word where the cursor is, in the box last typed in. */
  const put = (word: string) => {
    if (lastBox === "subject" && email && subjectBox.current) {
      const el = subjectBox.current;
      const i = el.selectionStart ?? s.subject.length;
      const j = el.selectionEnd ?? i;
      onChange({ subject: s.subject.slice(0, i) + word + s.subject.slice(j) });
      requestAnimationFrame(() => el.setSelectionRange(i + word.length, i + word.length));
      return;
    }
    const el = box.current;
    const i = lastBox === "none" ? body.length : (el?.selectionStart ?? body.length);
    const j = el?.selectionEnd ?? i;
    setBody(body.slice(0, i) + word + body.slice(j));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(i + word.length, i + word.length);
    });
  };

  return (
    <div className="stack msg-fields">
      {happensOnEvent(kind) ? (
        <p className="muted">
          {index === 1 && kind === "booking_confirm" ? "Goes when the website gave a price." : `Goes right after ${info.anchor}.`}
        </p>
      ) : (
        <div className="row">
          <InputText
            label={before ? "Days before" : "Days after"}
            inputMode="numeric"
            value={String(Math.abs(s.day))}
            onChange={(e) => {
              const n = Math.min(before ? 30 : 400, Number(e.target.value.replace(/\D/g, "") || "0"));
              onChange({ day: before ? -n : n });
            }}
          />
          <InputText
            label="Time"
            type="time"
            description="Empty means the same time of day it started"
            value={s.time ?? ""}
            onChange={(e) => onChange({ time: e.target.value || null })}
          />
        </div>
      )}
      {email ? (
        <label className="field wide">
          <span className="label">Subject</span>
          <span className="input">
            <input
              ref={subjectBox}
              value={s.subject}
              onFocus={() => setLastBox("subject")}
              onChange={(e) => onChange({ subject: e.target.value })}
            />
          </span>
        </label>
      ) : null}
      <label className="field wide">
        <span className="label">{email ? "Email" : "Text"}</span>
        <textarea
          ref={box}
          rows={email ? 9 : 5}
          value={body}
          onFocus={() => setLastBox("body")}
          onChange={(e) => setBody(e.target.value)}
        />
      </label>
      {!body.trim() ? <p className="msg-warn">Write something, or it cannot be saved.</p> : null}
      <div className="msg-fills">
        <span className="muted">Put in</span>
        {info.fills.map((f) => (
          <button key={f} type="button" className="chip" onClick={() => put(f)}>
            {FILL_NAMES[f] ?? f.slice(1, -1)}
          </button>
        ))}
      </div>
      <p className="muted msg-meta">
        {info.ending
          ? `Every ${email ? "email" : "text"} ends with "${fillAutoWords(info.ending, { link: "the link" })}". You cannot take it off. `
          : ""}
        {email ? "" : `${String(full.length)} letters, sent as ${String(parts)} text${parts === 1 ? "" : "s"}.`}
      </p>
      <div>
        <Button type="tertiary" size="small" icon="undo" onClick={onReset}>
          Use the starting words
        </Button>
      </div>
    </div>
  );
}

/** Links in a message, drawn blue like the phone or mail app does. */
function Linked({ text }: { text: string }) {
  const bits = text.split(/(https?:\/\/\S+)/g);
  return (
    <>
      {bits.map((b, i) =>
        i % 2 ? (
          <span key={i} className="msg-link">
            {b}
          </span>
        ) : (
          b
        ),
      )}
    </>
  );
}

function Preview({
  kind,
  d,
  step: s,
  index,
}: {
  kind: AutomationKind;
  d: AutomationDetail;
  step: AutomationStep;
  index: number;
}) {
  const business = d.words.business ?? "Your business";
  const when = whenOf(kind, s, index);
  if (s.channel === "sms") {
    const text = stepText(kind, s, wordsFor(kind, s, d.words));
    return (
      <figure className="msg-preview" aria-label="How the text looks on a phone">
        <figcaption className="muted">How {d.words.first ?? "the client"} sees it</figcaption>
        <div className="msg-phone">
          <div className="msg-phone-top">
            <span className="msg-avatar">{business.charAt(0).toUpperCase()}</span>
            <b>{business}</b>
            <span className="muted">Text message</span>
          </div>
          <div className="msg-phone-body">
            <span className="msg-stamp">{when}</span>
            <p className="msg-bubble">
              <Linked text={text} />
            </p>
          </div>
        </div>
      </figure>
    );
  }
  const subject = fillAutoWords(s.subject, wordsFor(kind, s, d.words));
  const body = stepEmail(kind, s, wordsFor(kind, s, d.words));
  return (
    <figure className="msg-preview" aria-label="How the email looks">
      <figcaption className="muted">How {d.words.first ?? "the client"} sees it</figcaption>
      <div className="msg-mail">
        <div className="msg-mail-top">
          <span className="msg-avatar">{business.charAt(0).toUpperCase()}</span>
          <span className="what">
            <b>{business}</b>
            <span className="muted">
              To {d.words.name ?? "the client"}, {lowerFirst(when)}
            </span>
          </span>
        </div>
        <h3 className="msg-mail-subject">{subject ? subject : <span className="muted">No subject</span>}</h3>
        <p className="msg-mail-body">
          <Linked text={body} />
        </p>
      </div>
    </figure>
  );
}
